import type {
  ToolAvailability,
  ToolCalibrationResult,
  ToolCalibrationStatus,
  ToolClassification,
  ToolCondition,
  ToolIssuability,
  ToolOwnership,
  ToolReturnCondition,
  ToolStatus,
} from "@common/client"
import {
  ToolAvailabilitySchema,
  ToolCalibrationResultSchema,
  ToolCalibrationStatusSchema,
  ToolClassificationSchema,
  ToolConditionSchema,
  ToolOwnershipSchema,
  ToolReturnConditionSchema,
  ToolStatusSchema,
} from "@common/client/schemas.gen"
import { differenceInCalendarDays, parseISO } from "date-fns"

export const STATUSES = ToolStatusSchema.enum

export const CALIBRATION_STATUSES = ToolCalibrationStatusSchema.enum

export const CLASSIFICATIONS = ToolClassificationSchema.enum

export const CONDITIONS = ToolConditionSchema.enum

export const OWNERSHIPS = ToolOwnershipSchema.enum

export const AVAILABILITIES = ToolAvailabilitySchema.enum

export const RETURN_CONDITIONS = ToolReturnConditionSchema.enum

export const CALIBRATION_RESULTS = ToolCalibrationResultSchema.enum

export function statusLabel(status: ToolStatus): string {
  switch (status) {
    case "available":
      return "Available"
    case "on_loan":
      return "On loan"
    case "in_calibration":
      return "In calibration"
    case "out_of_service":
      return "Out of service"
    case "retired":
      return "Retired"
  }
}

export function calibrationStatusLabel(status: ToolCalibrationStatus): string {
  switch (status) {
    case "not_required":
      return "Not calibrated"
    case "current":
      return "In cal"
    case "due_soon":
      return "Due soon"
    case "overdue":
      return "Out of cal"
  }
}

export function classificationLabel(
  classification: ToolClassification,
): string {
  switch (classification) {
    case "calibration_controlled":
      return "Calibration-controlled"
    case "accountable":
      return "Accountable"
    case "untracked":
      return "Untracked"
  }
}

export function conditionLabel(condition: ToolCondition): string {
  switch (condition) {
    case "serviceable":
      return "Serviceable"
    case "unserviceable":
      return "Unserviceable"
    case "quarantined":
      return "Quarantined"
  }
}

export function ownershipLabel(ownership: ToolOwnership): string {
  switch (ownership) {
    case "shop":
      return "Shop"
    case "personal":
      return "Personal (approved)"
    case "vendor":
      return "Vendor-loaned"
  }
}

export function availabilityLabel(availability: ToolAvailability): string {
  switch (availability) {
    case "permanent":
      return "Permanent, on premises"
    case "occasional":
      return "Occasional"
  }
}

export function returnConditionLabel(condition: ToolReturnCondition): string {
  switch (condition) {
    case "serviceable":
      return "Serviceable"
    case "damaged":
      return "Damaged"
    case "quarantine":
      return "Quarantine"
  }
}

export function calibrationResultLabel(result: ToolCalibrationResult): string {
  switch (result) {
    case "pass":
      return "Pass"
    case "adjusted":
      return "Adjusted"
    case "fail":
      return "Fail"
  }
}

export function issueBlockedReason(
  issuability: ToolIssuability,
): string | null {
  switch (issuability) {
    case "issuable":
    case "unavailable":
      return null
    case "untracked":
      return "Untracked tools are not issued"
    case "out_of_calibration":
      return "Not issuable while out of calibration"
  }
}

export function daysUntil(isoDate: string): number {
  return differenceInCalendarDays(parseISO(isoDate), new Date())
}

export function dueInLabel(isoDate: string): string {
  const days = daysUntil(isoDate)
  if (days < 0) return `${-days} d overdue`
  return days === 0 ? "Today" : `${days} d`
}

export function options<T extends string>(
  values: readonly T[],
  label: (value: T) => string,
): { value: T; label: string }[] {
  return values.map((value) => ({ value, label: label(value) }))
}
